import { useEffect, useState } from 'react';
import { Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';

const JOURS = [[1, 'Lun'], [2, 'Mar'], [3, 'Mer'], [4, 'Jeu'], [5, 'Ven'], [6, 'Sam'], [7, 'Dim']];
const AUTOS = [
  ['planning_lendemain', 'Planning du lendemain', 'Chaque chauffeur reçoit sur Telegram son chantier du lendemain', 'heure'],
  ['alerte_arrivee', 'Arrivée sur parcelle', "Vous êtes prévenu quand un chauffeur arrive sur la parcelle de son chantier", 'rayon'],
  ['brouillon_facture', 'Brouillon de facture', 'Un brouillon est préparé quand un chantier est terminé (rien n\'est envoyé au client)'],
  ['nouvelle_parcelle', 'Nouvelle parcelle', 'Vous êtes prévenu dès qu\'une parcelle est ajoutée'],
  ['nouveau_client', 'Nouveau client', 'Vous êtes prévenu dès qu\'un client est ajouté'],
  ['chantier_a_facturer', 'Chantiers à facturer', 'Chaque jour, la liste des chantiers terminés pas encore facturés', 'heure'],
];

const HEURES = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`);

const ChoixHeure = ({ id, valeur, onChange }) => (
  <select data-testid={id} value={valeur} onChange={(e) => onChange(e.target.value)}
    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
    {!HEURES.includes(valeur) && <option value={valeur}>{valeur}</option>}
    {HEURES.map((h) => <option key={h} value={h}>{h.replace(':', ' h ')}</option>)}
  </select>
);

const Nombre = ({ id, valeur, onChange, min, max, suffixe }) => (
  <div className="flex items-center gap-2">
    <Input data-testid={id} type="number" min={min} max={max} className="w-24" value={valeur ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />
    <span className="text-sm text-muted-foreground">{suffixe}</span>
  </div>
);

const OngletGPS = ({ gps, set, chauffeurs }) => (
  <div className="space-y-5">
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-1"><Label>Envoi de la position toutes les</Label>
        <Nombre id="reglage-frequence" valeur={gps.frequence_min} min={1} max={60} suffixe="min" onChange={(v) => set({ frequence_min: v })} /></div>
      <div className="space-y-1"><Label>Inactif après</Label>
        <Nombre id="reglage-delai-inactif" valeur={gps.delai_inactif_min} min={1} max={240} suffixe="min sans nouvelles" onChange={(v) => set({ delai_inactif_min: v })} /></div>
      <div className="space-y-1"><Label>Début du suivi</Label>
        <ChoixHeure id="reglage-heure-debut" valeur={gps.heure_debut} onChange={(v) => set({ heure_debut: v })} /></div>
      <div className="space-y-1"><Label>Fin du suivi</Label>
        <ChoixHeure id="reglage-heure-fin" valeur={gps.heure_fin} onChange={(v) => set({ heure_fin: v })} /></div>
    </div>
    <div className="space-y-2"><Label>Jours suivis</Label>
      <div className="flex flex-wrap gap-2">
        {JOURS.map(([n, l]) => {
          const on = gps.jours.includes(n);
          return (
            <Button key={n} type="button" size="sm" variant={on ? 'default' : 'outline'} data-testid={`reglage-jour-${n}`}
              className={on ? '' : 'text-foreground hover:bg-primary/10 hover:text-foreground'}
              onClick={() => set({ jours: on ? gps.jours.filter((j) => j !== n) : [...gps.jours, n].sort() })}>{l}</Button>
          );
        })}
      </div>
    </div>
    <div className="space-y-2"><Label>Chauffeurs suivis</Label>
      {chauffeurs.length === 0 && <p className="text-sm text-muted-foreground">Aucun chauffeur dans cette société.</p>}
      {chauffeurs.map((c) => (
        <label key={c.id} className="flex items-center gap-2 text-sm">
          <Checkbox data-testid={`reglage-chauffeur-${c.id}`} checked={!gps.chauffeurs_exclus.includes(c.id)}
            onCheckedChange={(v) => set({ chauffeurs_exclus: v ? gps.chauffeurs_exclus.filter((x) => x !== c.id) : [...gps.chauffeurs_exclus, c.id] })} />
          {c.nom}
        </label>
      ))}
    </div>
  </div>
);

const OngletAutos = ({ autos, set }) => (
  <div className="space-y-3">
    {AUTOS.map(([cle, titre, desc, extra]) => (
      <div key={cle} className="rounded-lg border p-3 space-y-2" data-testid={`auto-${cle}`}>
        <div className="flex items-start justify-between gap-3">
          <div><p className="font-medium text-sm">{titre}</p><p className="text-xs text-muted-foreground">{desc}</p></div>
          <Switch data-testid={`auto-${cle}-switch`} checked={autos[cle].actif} onCheckedChange={(v) => set(cle, { actif: v })} />
        </div>
        {autos[cle].actif && extra === 'heure' && (
          <Nombre id={`auto-${cle}-heure`} valeur={autos[cle].heure} min={0} max={23} suffixe="h (heure d'envoi)" onChange={(v) => set(cle, { heure: v })} />)}
        {autos[cle].actif && extra === 'rayon' && (
          <Nombre id={`auto-${cle}-rayon`} valeur={autos[cle].rayon_m} min={50} max={5000} suffixe="m autour de la parcelle" onChange={(v) => set(cle, { rayon_m: v })} />)}
      </div>
    ))}
    <p className="text-xs text-muted-foreground">Les messages sont envoyés par Valentine sur Telegram.</p>
  </div>
);

// Réglages du suivi GPS et des envois automatiques (administrateur uniquement)
export const ReglagesSuiviDialog = ({ onEnregistre }) => {
  const [ouvert, setOuvert] = useState(false);
  const [data, setData] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    if (!ouvert) return;
    api.get('/reglages-suivi').then((r) => setData(r.data))
      .catch((e) => { toast.error(e?.response?.data?.detail || 'Réglages indisponibles'); setOuvert(false); });
  }, [ouvert]);

  const enregistrer = async () => {
    setEnvoi(true);
    try {
      await api.put('/reglages-suivi', { gps: data.gps, automatisations: data.automatisations });
      toast.success('Réglages enregistrés');
      setOuvert(false);
      onEnregistre?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Valeur invalide, vérifiez les champs');
    } finally { setEnvoi(false); }
  };

  return (
    <Dialog open={ouvert} onOpenChange={setOuvert}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="btn-reglages-suivi"><Settings2 className="w-4 h-4 mr-1" />Réglages</Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="reglages-suivi-dialog">
        <DialogHeader>
          <DialogTitle>Réglages du suivi</DialogTitle>
          <DialogDescription>Le suivi GPS s'applique à la société active ; les envois automatiques s'appliquent à vos chantiers.</DialogDescription>
        </DialogHeader>
        {!data ? <p className="text-sm text-muted-foreground">Chargement…</p> : (
          <Tabs defaultValue="gps">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="gps" data-testid="onglet-reglages-gps">Suivi GPS</TabsTrigger>
              <TabsTrigger value="autos" data-testid="onglet-reglages-autos">Envois de Valentine</TabsTrigger>
            </TabsList>
            <TabsContent value="gps" className="pt-3">
              <OngletGPS gps={data.gps} chauffeurs={data.chauffeurs} set={(m) => setData({ ...data, gps: { ...data.gps, ...m } })} />
            </TabsContent>
            <TabsContent value="autos" className="pt-3">
              <OngletAutos autos={data.automatisations}
                set={(cle, m) => setData({ ...data, automatisations: { ...data.automatisations, [cle]: { ...data.automatisations[cle], ...m } } })} />
            </TabsContent>
          </Tabs>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOuvert(false)} data-testid="btn-reglages-annuler">Annuler</Button>
          <Button onClick={enregistrer} disabled={!data || envoi} data-testid="btn-reglages-enregistrer">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReglagesSuiviDialog;
